import { useEffect } from 'react';
import { useMutation } from 'platform/useMutation';
import { gql, MutationHookOptions } from '@apollo/client';

import { ClosingTaskSubmissionEventName } from 'sellDirect/graphql/useUpdateClosingTasks';
import {
  SubmitDigitalCheckout,
  SubmitDigitalCheckoutVariables,
} from 'sellDirect/graphql/__generated__/SubmitDigitalCheckout';
import { DigitalCheckoutInput } from '__generated__/globalTypes';
import { useObservability } from 'shared/helpers/observability';
import { useOfferContext } from 'sellDirect/OfferContext';

const SubmitDigitalCheckoutMutation = gql`
  mutation SubmitDigitalCheckout($webOfferToken: String!, $digitalCheckout: DigitalCheckoutInput!) {
    seller {
      submitDigitalCheckout(webOfferToken: $webOfferToken, digitalCheckout: $digitalCheckout) {
        declaration
        approvedAt
        submittedAt
        photolessOverride
      }
    }
  }
`;

/** Hook to submit the digital checkout form */
export const useSubmitDigitalCheckout = (options?: MutationHookOptions<SubmitDigitalCheckout>) => {
  const { offer } = useOfferContext();
  const { trackEvent, trackError } = useObservability();

  const [submit, { data, loading, error }] = useMutation<
    SubmitDigitalCheckout,
    SubmitDigitalCheckoutVariables
  >(SubmitDigitalCheckoutMutation, {
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache',
    refetchQueries: () => ['GetSellDirect'],
    onCompleted: (data) => {
      trackEvent(ClosingTaskSubmissionEventName, 'digital-checkout');
      if (options?.onCompleted) {
        options.onCompleted(data);
      }
    },
    onError: options?.onError,
    errorPolicy: options?.errorPolicy,
  });

  const submitDigitalCheckout = (digitalCheckout: DigitalCheckoutInput) => {
    if (!offer?.leadToken) {
      trackError('missing-web-offer-token');
      return;
    }
    submit({
      variables: {
        webOfferToken: offer.leadToken,
        digitalCheckout,
      },
    }).catch((error) => {
      trackError('submit-digital-checkout', error, {
        digitalCheckout: JSON.stringify(digitalCheckout),
      });
    });
  };

  useEffect(() => {
    if (error) {
      trackError('submit-digital-checkout', error);
    }
    // eslint-disable-next-line
  }, [error]);

  return { submitDigitalCheckout, loading, data, error };
};
